import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { 
  Mail, 
  Send, 
  Inbox, 
  ArrowLeft,
  User,
  Clock,
  Trash2,
  Check,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";

const positionLabels = {
  secretary: "Γραμματέας",
  department_head: "Προϊστάμενος Τμήματος",
  judge: "Δικαστής",
  council_president: "Πρόεδρος Τριμελούς",
  council_member: "Μέλος Τριμελούς"
};

const Messages = () => {
  const { user } = useAuth();
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [sending, setSending] = useState(false);
  const [composeForm, setComposeForm] = useState({
    recipient_id: "",
    subject: "",
    content: ""
  });

  useEffect(() => {
    fetchMessages();
    fetchUsers();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const [inboxRes, sentRes] = await Promise.all([
        axios.get(`${API}/messages/inbox`),
        axios.get(`${API}/messages/sent`)
      ]);
      setInbox(inboxRes.data);
      setSent(sentRes.data);
    } catch (error) {
      toast.error("Σφάλμα φόρτωσης μηνυμάτων");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/list`);
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users");
    }
  };

  const handleSendMessage = async () => {
    if (!composeForm.recipient_id || !composeForm.subject || !composeForm.content) {
      toast.error("Συμπληρώστε όλα τα πεδία");
      return;
    }

    try {
      setSending(true);
      await axios.post(`${API}/messages`, composeForm);
      toast.success("Το μήνυμα στάλθηκε");
      setShowCompose(false);
      setComposeForm({ recipient_id: "", subject: "", content: "" });
      fetchMessages();
    } catch (error) {
      toast.error("Σφάλμα αποστολής");
    } finally {
      setSending(false);
    }
  };

  const handleReadMessage = async (message) => {
    if (!message.is_read && message.recipient_id === user?.id) {
      await axios.put(`${API}/messages/${message.id}/read`);
      fetchMessages();
    }
    setSelectedMessage(message);
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/messages/${messageId}`);
      toast.success("Το μήνυμα διαγράφηκε");
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      toast.error("Σφάλμα διαγραφής");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString("el-GR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const unreadCount = inbox.filter(m => !m.is_read).length;

  const MessageList = ({ messages, type }) => (
    <div className="space-y-2 max-h-[500px] overflow-y-auto">
      {messages.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{type === "inbox" ? "Δεν έχετε μηνύματα" : "Δεν έχετε στείλει μηνύματα"}</p>
        </div>
      ) : (
        messages.map((message) => (
          <div
            key={message.id}
            className={`p-4 rounded-lg border transition-colors cursor-pointer ${
              !message.is_read && type === "inbox"
                ? "bg-primary/5 border-primary/20 hover:bg-primary/10"
                : "bg-white border-slate-200 hover:bg-slate-50"
            }`}
            onClick={() => handleReadMessage(message)}
            data-testid={`message-${message.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${!message.is_read && type === "inbox" ? "text-primary" : "text-slate-900"}`}>
                    {type === "inbox" ? message.sender_name : message.recipient_name}
                  </span>
                  {!message.is_read && type === "inbox" && (
                    <Badge className="bg-primary text-white text-xs">Νέο</Badge>
                  )}
                </div>
                <p className="text-sm text-slate-700 mt-0.5 truncate">{message.subject}</p>
                <p className="text-xs text-slate-500 mt-1">{formatDate(message.created_at)}</p>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Μηνύματα</h1>
          <p className="text-slate-500 mt-1">Επικοινωνία με άλλους χρήστες</p>
        </div>
        <Button onClick={() => setShowCompose(true)} data-testid="compose-btn">
          <Send className="w-4 h-4 mr-2" />
          Νέο Μήνυμα
        </Button>
      </div>

      {/* Message View or List */}
      {selectedMessage ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => setSelectedMessage(null)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex-1">
                <CardTitle className="text-lg">{selectedMessage.subject}</CardTitle>
                <CardDescription>
                  {selectedMessage.sender_id === user?.id 
                    ? `Προς: ${selectedMessage.recipient_name}` 
                    : `Από: ${selectedMessage.sender_name}`
                  } • {formatDate(selectedMessage.created_at)}
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-red-500"
                onClick={() => handleDeleteMessage(selectedMessage.id)}
                data-testid="delete-message-btn"
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 whitespace-pre-wrap">{selectedMessage.content}</p>
            
            {selectedMessage.recipient_id === user?.id && (
              <Button 
                className="mt-4"
                variant="outline"
                onClick={() => {
                  setComposeForm({
                    recipient_id: selectedMessage.sender_id,
                    subject: `RE: ${selectedMessage.subject}`,
                    content: ""
                  });
                  setShowCompose(true);
                }}
                data-testid="reply-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Απάντηση
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="inbox">
          <TabsList>
            <TabsTrigger value="inbox" className="gap-2" data-testid="inbox-tab">
              <Inbox className="w-4 h-4" />
              Εισερχόμενα
              {unreadCount > 0 && (
                <Badge className="bg-primary text-white ml-1">{unreadCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent" data-testid="sent-tab">
              <Send className="w-4 h-4 mr-2" />
              Απεσταλμένα
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inbox" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : (
                  <MessageList messages={inbox} type="inbox" />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sent" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : (
                  <MessageList messages={sent} type="sent" />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Compose Dialog */}
      <Dialog open={showCompose} onOpenChange={setShowCompose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Νέο Μήνυμα</DialogTitle>
            <DialogDescription>
              Αποστολή μηνύματος σε άλλον χρήστη
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Παραλήπτης</Label>
              <Select 
                value={composeForm.recipient_id} 
                onValueChange={(id) => setComposeForm({...composeForm, recipient_id: id})}
              >
                <SelectTrigger data-testid="recipient-select">
                  <SelectValue placeholder="Επιλέξτε παραλήπτη..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      <div className="flex items-center gap-2">
                        <span>{u.full_name}</span>
                        <span className="text-xs text-slate-500">
                          ({positionLabels[u.position] || u.position})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Θέμα</Label>
              <Input
                value={composeForm.subject}
                onChange={(e) => setComposeForm({...composeForm, subject: e.target.value})}
                placeholder="Θέμα μηνύματος"
                data-testid="subject-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Μήνυμα</Label>
              <Textarea
                value={composeForm.content}
                onChange={(e) => setComposeForm({...composeForm, content: e.target.value})}
                placeholder="Γράψτε το μήνυμά σας..."
                rows={5}
                data-testid="content-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompose(false)}>
              Ακύρωση
            </Button>
            <Button onClick={handleSendMessage} disabled={sending} data-testid="send-message-btn">
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Αποστολή...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Αποστολή
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
